import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import editorStore from "../../stores/EditorStore";
import BannerTextBar from "./BannerTextBar";
import { POINTS_PER_INCH } from "./bannerConfig";

const RULER = 20;
const RULER_STEPS = [0.25, 0.5, 1, 2, 3, 6, 12, 24, 48, 96];

/** Draws one inch ruler. `origin` is the screen position of the page edge, `ppi` screen pixels per inch. */
function drawRuler(el: HTMLCanvasElement, length: number, origin: number, ppi: number, vertical: boolean) {
  const dpr = window.devicePixelRatio || 1;
  const w = vertical ? RULER : length;
  const h = vertical ? length : RULER;
  if (el.width !== w * dpr || el.height !== h * dpr) {
    el.width = w * dpr; el.height = h * dpr;
    el.style.width = `${w}px`; el.style.height = `${h}px`;
  }
  const ctx = el.getContext("2d");
  if (!ctx || ppi <= 0) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "#9ca3af";
  ctx.fillStyle = "#6b7280";
  ctx.font = "9px 'Plus Jakarta Sans', sans-serif";
  ctx.lineWidth = 1;

  const step = RULER_STEPS.find(s => s * ppi >= 60) ?? RULER_STEPS[RULER_STEPS.length - 1];
  const minor = step / 4;
  const first = Math.floor(-origin / ppi / minor) * minor;
  const last = (length - origin) / ppi;
  ctx.beginPath();
  for (let i = first; i <= last; i += minor) {
    const p = Math.round(origin + i * ppi) + 0.5;
    const isMajor = Math.abs(i / step - Math.round(i / step)) < 1e-6;
    const tick = isMajor ? RULER : RULER / 4;
    if (vertical) { ctx.moveTo(RULER - tick, p); ctx.lineTo(RULER, p); }
    else { ctx.moveTo(p, RULER - tick); ctx.lineTo(p, RULER); }
    if (isMajor) {
      const label = String(Math.round(i * 100) / 100);
      if (vertical) {
        ctx.save(); ctx.translate(9, p + 3); ctx.rotate(-Math.PI / 2); ctx.fillText(label, 0, 0); ctx.restore();
      } else {
        ctx.fillText(label, p + 3, 9);
      }
    }
  }
  ctx.stroke();
  ctx.strokeStyle = "#e5e7eb";
  ctx.beginPath();
  if (vertical) { ctx.moveTo(RULER - 0.5, 0); ctx.lineTo(RULER - 0.5, h); }
  else { ctx.moveTo(0, RULER - 0.5); ctx.lineTo(w, RULER - 0.5); }
  ctx.stroke();
}

const BannerWorkspace = observer(() => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const topRulerRef = useRef<HTMLCanvasElement>(null);
  const leftRulerRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;
    const container = containerRef.current;
    editorStore.initCanvas(canvasRef.current, container);

    const fc = editorStore.getCanvas();
    if (fc) {
      // Keep the Fabric background transparent so the workspace grey shows around the page.
      // Page loads restore backgroundColor from JSON, so re-apply after each render.
      (fc as any).backgroundColor = "";
      fc.on("after:render", () => {
        if ((fc as any).backgroundColor !== "") {
          (fc as any).backgroundColor = "";
          fc.requestRenderAll();
        }
        const vt = fc.viewportTransform || [1, 0, 0, 1, 0, 0];
        const ppi = vt[0] * POINTS_PER_INCH;
        if (topRulerRef.current) drawRuler(topRulerRef.current, container.clientWidth, vt[4], ppi, false);
        if (leftRulerRef.current) drawRuler(leftRulerRef.current, container.clientHeight, vt[5], ppi, true);
      });
      fc.requestRenderAll();
    }

    const ro = new ResizeObserver(() => editorStore.fitCanvasToContainer());
    ro.observe(container);
    return () => { ro.disconnect(); editorStore.destroy(); };
  }, []);

  useEffect(() => {
    // Fabric's upper canvas captures pointer events, so listen on the container
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const rect = el.getBoundingClientRect();
      if (!editorStore.isScreenPointOnPage(e.clientX - rect.left, e.clientY - rect.top)) return;
      e.preventDefault();
      editorStore.zoomAtPoint(e.clientX - rect.left, e.clientY - rect.top, e.deltaY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const active = editorStore.getCanvas()?.getActiveObject();
      if (active && (active as any).isEditing) return;
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); editorStore.deleteSelected(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); e.shiftKey ? editorStore.history.redo() : editorStore.history.undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === "y") { e.preventDefault(); editorStore.history.redo(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("application/x-image-item");
    if (!data || !containerRef.current) return;
    const item = JSON.parse(data) as { id: number; src: string };
    const rect = containerRef.current.getBoundingClientRect();
    const vt = editorStore.getCanvas()?.viewportTransform || [1, 0, 0, 1, 0, 0];
    const x = (e.clientX - rect.left - vt[4]) / vt[0];
    const y = (e.clientY - rect.top - vt[5]) / vt[3];
    addPhoto(item, { x, y });
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden bg-[#f5f5f5]"
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
      onDrop={handleDrop}
    >
      <div className="absolute inset-0">
        <canvas ref={canvasRef} />
      </div>

      <canvas ref={topRulerRef} className="pointer-events-none absolute left-0 top-0 z-10" />
      <canvas ref={leftRulerRef} className="pointer-events-none absolute left-0 top-0 z-10" />
      <div className="pointer-events-none absolute left-0 top-0 z-10 h-5 w-5 border-b border-r border-gray-200 bg-white" />

      <div className="pointer-events-none absolute bottom-3 left-8 z-10 flex gap-4 rounded bg-white/90 px-3 py-1.5 font-jakarta text-[10px] text-gray-600 shadow-sm">
        <span className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-red-500" /> Trim line — anything outside is cut off</span>
        <span className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-brand" /> Safety line — keep text inside</span>
      </div>

      <BannerTextBar />
    </div>
  );
});

/** Adds an uploaded photo, fitted inside the page. Centred on `at` (page units) when given, else on the page. */
export function addPhoto(item: { id: number; src: string }, at?: { x: number; y: number }) {
  const { width, height } = editorStore;
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(width / img.width, height / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    const cx = at?.x ?? width / 2;
    const cy = at?.y ?? height / 2;
    editorStore.activePage.addElement({
      type: "image", src: item.src, uploadId: item.id,
      width: w, height: h,
      x: cx - w / 2, y: cy - h / 2,
    });
  };
  img.src = item.src;
}

export default BannerWorkspace;
